import { calendarDate } from '../services/financialMath';
import { accountingEntries, summarizeTransactions, selectAccountingEntries } from '../services/financialLedger';
import React, { useState, useMemo, useEffect } from 'react';
import {useNavigate,useLocation} from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../contexts/AppContext';
import { dbService } from '../services/dbService';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subDays } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import QuickAddForm from '../components/QuickAddForm';

import CategoryManager from '../components/CategoryManager';

import * as FiIcons from 'react-icons/fi';
import {exportService} from '../services/exportService';
import {matchCategoryRule} from '../services/categorizationRules';
import {matchesSearch,transactionSearchFields} from '../services/smartSearch.js';


// Kept as a private TransactionEditForm module to preserve the original component behavior.
const TransactionEditForm = (()=>{







const { FiX, FiSave, FiPlus, FiMinus } = FiIcons;

const TransactionEditForm = ({ transaction, onClose }) => {
  const { state, actions } = useApp();
  const { categories, people, settings } = state;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastAutomaticCategory, setLastAutomaticCategory] = useState('');
  const [formData, setFormData] = useState({
    date: transaction.date,
    type: transaction.type,
    amount: transaction.amount.toString(),
    category: transaction.category,
    payee: transaction.payee || '',
    notes: transaction.notes || '',
    tags: transaction.tags ? transaction.tags.join(', ') : '',
    account: transaction.account || 'Main Account',
    person: transaction.person || '',
    status: transaction.status || 'posted',
  });

  const expenseCategories = categories.filter(c => c.type === 'expense');
  const incomeCategories = categories.filter(c => c.type === 'income');
  const currentCategories = formData.type === 'expense' ? expenseCategories : incomeCategories;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || !formData.category || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const updatedTransaction = {
        ...transaction,
        ...formData,
        amount: parseFloat(formData.amount),
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        updatedAt: new Date(),
      };

      // Update in database
      const savedTransaction = await dbService.updateTransaction(transaction.id, updatedTransaction);
      
      // Update in state from the persisted record, including cash confirmation.
      actions.updateTransaction(savedTransaction);
      
      onClose();
    } catch (error) {
      console.error('Failed to update transaction:', error);
      alert('Failed to update transaction. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    if (field === 'category') setLastAutomaticCategory('');
    if (field === 'payee') {
      const rule = matchCategoryRule({payee:value,type:formData.type}, settings.categorizationRules || [], categories);
      if (rule && (!formData.category || formData.category === lastAutomaticCategory)) {
        setLastAutomaticCategory(rule.category);
        setFormData(prev => ({...prev,payee:value,category:rule.category}));
        return;
      }
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-select first category when type changes
    if (field === 'type' && formData.category) {
      setLastAutomaticCategory('');
      const newCategories = value === 'expense' ? expenseCategories : incomeCategories;
      if (newCategories.length > 0 && !newCategories.find(c => c.name === formData.category)) {
        setFormData(prev => ({ ...prev, category: newCategories[0].name }));
      }
    }
  };

  const activeRule = matchCategoryRule({payee:formData.payee,type:formData.type}, settings.categorizationRules || [], categories);

  const animationProps = settings.reducedMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.9 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
        transition: { duration: 0.2 },
      };

  return (
    <motion.div
      className="mobile-full-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      {...animationProps}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        layout={!settings.reducedMotion}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit Transaction</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors duration-200"
            type="button"
          >
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleChange('date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Type and Amount */}
          <div className="flex space-x-2">
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                type="button"
                onClick={() => handleChange('type', 'expense')}
                disabled={isSubmitting}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  formData.type === 'expense'
                    ? 'bg-danger-500 text-white'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={FiMinus} className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handleChange('type', 'income')}
                disabled={isSubmitting}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  formData.type === 'income'
                    ? 'bg-success-500 text-white'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
              </button>
            </div>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={formData.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
              disabled={isSubmitting}
            >
              <option value="">Select category</option>
              {currentCategories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
              {activeRule && <p className="text-xs text-blue-600 mt-1" role="status">Merchant rule: {activeRule.pattern} → {activeRule.category}. You can change the category manually.</p>}
          </div>

          {/* Payee */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payee
            </label>
            <input
              type="text"
              placeholder="Payee (optional)"
              value={formData.payee}
              onChange={(e) => handleChange('payee', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              placeholder="Notes (optional)"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              disabled={isSubmitting}
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <input
              type="text"
              placeholder="Tags (comma separated)"
              value={formData.tags}
              onChange={(e) => handleChange('tags', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>

          {/* Account */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account
            </label>
            <input
              type="text"
              placeholder="Account"
              value={formData.account}
              onChange={(e) => handleChange('account', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>

          {/* Person */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Person
            </label>
            <select
              value={formData.person}
              onChange={(e) => handleChange('person', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSubmitting}
            >
              <option value="">No person</option>
              {people.map((person) => (
                <option key={person.id} value={person.name}>
                  {person.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSubmitting}
            >
              <option value="posted">Posted</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg font-medium transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.amount || !formData.category || isSubmitting}
              className="flex-1 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors duration-200"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Updating...
                </div>
              ) : (
                <>
                  <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                  Update Transaction
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};



return TransactionEditForm;
})();

// Kept as a private ExportDialog module to preserve the original component behavior.
const ExportDialog = (()=>{





const { FiX, FiDownload, FiFileText, FiDatabase, FiCheck } = FiIcons;

const ExportDialog = ({ 
  transactions, 
  filters, 
  selectedTransactions,
  visibleTransactions,
  settings,
  onClose 
}) => {
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportScope, setExportScope] = useState('filtered');
  const [isExporting, setIsExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);

  // Calculate what will be exported
  const getExportPreview = () => {
    if (exportScope === 'selected' && selectedTransactions?.length > 0) {
      return {
        count: selectedTransactions.length,
        description: `${selectedTransactions.length} selected transactions`
      };
    } else if (exportScope === 'all') {
      return {
        count: transactions.length,
        description: `All ${transactions.length} transactions`
      };
    } else {
      // Filtered transactions
      const filtered = visibleTransactions || exportService.applyFilters(transactions, filters);
      return {
        count: filtered.length,
        description: `${filtered.length} filtered transactions`
      };
    }
  };

  const preview = getExportPreview();

  const handleExport = async () => {
    if (preview.count === 0) {
      setExportResult({
        success: false,
        error: 'No transactions to export'
      });
      return;
    }

    setIsExporting(true);
    setExportResult(null);

    try {
      let transactionsToExport;

      if (exportScope === 'selected' && selectedTransactions?.length > 0) {
        transactionsToExport = transactions.filter(t => 
          selectedTransactions.includes(t.id)
        );
      } else if (exportScope === 'all') {
        transactionsToExport = transactions;
      } else {
        transactionsToExport = visibleTransactions || exportService.applyFilters(transactions, filters);
      }

      const result = await exportService.exportTransactions(
        transactionsToExport, 
        exportFormat, 
        settings
      );

      if (result.success) {
        setExportResult({
          success: true,
          filename: result.filename,
          count: result.transactionCount,
          format: result.format
        });
      } else if (result.cancelled) {
        setExportResult({
          success: false,
          cancelled: true
        });
      }
    } catch (error) {
      console.error('Export failed:', error);
      setExportResult({
        success: false,
        error: error.message
      });
    } finally {
      setIsExporting(false);
    }
  };

  const animationProps = settings?.reducedMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.9 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
        transition: { duration: 0.2 },
      };

  return (
    <motion.div
      className="mobile-full-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      {...animationProps}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        layout={!settings?.reducedMotion}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Export Transactions</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors duration-200"
            type="button"
          >
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Export Scope */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What to export
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="filtered"
                  checked={exportScope === 'filtered'}
                  onChange={(e) => setExportScope(e.target.value)}
                  className="mr-2 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Current filtered view</span>
              </label>
              
              {selectedTransactions?.length > 0 && (
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="selected"
                    checked={exportScope === 'selected'}
                    onChange={(e) => setExportScope(e.target.value)}
                    className="mr-2 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Selected transactions only</span>
                </label>
              )}
              
              <label className="flex items-center">
                <input
                  type="radio"
                  value="all"
                  checked={exportScope === 'all'}
                  onChange={(e) => setExportScope(e.target.value)}
                  className="mr-2 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">All transactions</span>
              </label>
            </div>
          </div>

          {/* Export Format */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Export format
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setExportFormat('csv')}
                className={`flex items-center justify-center p-3 border rounded-lg transition-colors duration-200 ${
                  exportFormat === 'csv'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <SafeIcon icon={FiFileText} className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">CSV</span>
              </button>
              <button
                onClick={() => setExportFormat('json')}
                className={`flex items-center justify-center p-3 border rounded-lg transition-colors duration-200 ${
                  exportFormat === 'json'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <SafeIcon icon={FiDatabase} className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">JSON</span>
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-1">Export Preview</h4>
            <p className="text-sm text-gray-600">{preview.description}</p>
            <p className="text-xs text-gray-500 mt-1">
              Format: {exportFormat.toUpperCase()}
              {exportFormat === 'csv' && ' (Excel compatible)'}
              {exportFormat === 'json' && ' (with summary statistics)'}
            </p>
          </div>

          {/* Export Result */}
          {exportResult && (
            <div className={`p-3 rounded-lg ${
              exportResult.success 
                ? 'bg-success-100 text-success-700 border border-success-200'
                : exportResult.cancelled
                ? 'bg-warning-100 text-warning-700 border border-warning-200'
                : 'bg-danger-100 text-danger-700 border border-danger-200'
            }`}>
              <div className="flex items-center">
                {exportResult.success && <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />}
                <span className="text-sm">
                  {exportResult.success && `Successfully exported ${exportResult.count} transactions to ${exportResult.filename}`}
                  {exportResult.cancelled && 'Export cancelled by user'}
                  {exportResult.error && `Export failed: ${exportResult.error}`}
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200"
            >
              {exportResult?.success ? 'Close' : 'Cancel'}
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting || preview.count === 0}
              className="flex-1 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors duration-200"
            >
              {isExporting ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Exporting...
                </div>
              ) : (
                <>
                  <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
                  Export {preview.count} Transactions
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};



return ExportDialog;
})();

const NAVY='#032D60';
const PERIODS=[
 {value:'thismonth',label:'This Month'},
 {value:'lastmonth',label:'Last Month'},
 {value:'last3',label:'Last 3 Months'},
 {value:'last12',label:'Last 12 Months'},
 {value:'thisyear',label:'This Year'},
 {value:'all',label:'All Dates'},
 {value:'custom',label:'Custom Range'}
];
const BASE_FILTERS={type:'',category:'',account:'',status:'',searchText:'',datePreset:'thismonth',customFrom:'',customTo:''};
const TX_COLUMNS=[
 {key:'date',label:'Date'},
 {key:'type',label:'Type'},
 {key:'category',label:'Category'},
 {key:'payee',label:'Payee / Merchant'},
 {key:'amount',label:'Amount'},
 {key:'account',label:'Account'},
 {key:'status',label:'Status'},
 {key:'notes',label:'Notes / Person'}
];
const sortText=new Intl.Collator('en-AU',{numeric:true,sensitivity:'base'});
const sortKey=(row,key)=>{
 if(key==='date')return calendarDate(row.date)||'';
 if(key==='amount'){
   const amount=Number(row.amount)||0;
   return row.type==='income'?amount:-amount;
 }
 if(key==='notes')return row.notes||row.person||'';
 if(key==='status')return row.status==='posted'?'completed':(row.status||'pending');
 return String(row[key]??'');
};
function compareTransactionRows(a,b,sort){
 const va=sortKey(a,sort.key),vb=sortKey(b,sort.key);
 const comparison=typeof va==='number'&&typeof vb==='number'?va-vb:sortText.compare(String(va),String(vb));
 const ordered=(sort.direction==='asc'?1:-1)*comparison;
 return ordered||sortText.compare(String(a.id),String(b.id));
}
function rangeFromPreset(f,now){
 if(f.datePreset==='all')return null;
 if(f.datePreset==='custom'){
   if(!f.customFrom||!f.customTo||f.customFrom>f.customTo)return null;
   return {start:f.customFrom,end:f.customTo};
 }
 const first=new Date(now.getFullYear(),now.getMonth(),1,12);
 const last=endOfMonth(first);
 const date=r=>calendarDate(r);
 if(f.datePreset==='lastmonth'){
   const month=subMonths(first,1);
   return {start:date(startOfMonth(month)),end:date(endOfMonth(month))};
 }
 if(f.datePreset==='last3')return {start:date(startOfMonth(subMonths(first,2))),end:date(last)};
 if(f.datePreset==='last12')return {start:date(startOfMonth(subMonths(first,11))),end:date(last)};
 if(f.datePreset==='thisyear')return {start:date(startOfYear(now)),end:date(endOfYear(now))};
 return {start:date(first),end:date(last)};
}
function transactionMatches(row,f,range){
 if(!row||row.isDeleted)return false;
 const date=calendarDate(row.date);
 if(range&&(!date||date<range.start||date>range.end))return false;
 if(f.type&&row.type!==f.type)return false;
 if(f.category&&String(row.category)!==String(f.category))return false;
 if(f.account&&String(row.account||'Main Account')!==f.account)return false;
 if(f.status&&String(row.status||'pending')!==f.status)return false;
 if(f.searchText&&!matchesSearch(transactionSearchFields(row),f.searchText))return false;
 return true;
}
function transactionIsRecurring(row){
 return Boolean(row.isRecurring||row.recurringRuleId!=null||row.recurringId!=null);
}
const txButton='inline-flex min-h-10 items-center justify-center gap-2 rounded-xl px-3.5 py-2.5 text-xs sm:text-sm font-semibold transition-all cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0176D3] active:translate-y-px';
const txSelect='min-h-11 rounded-xl border border-[#dbe5f2] bg-white px-3 text-sm font-medium text-[#25476d] focus-visible:outline-2 focus-visible:outline-[#0176D3] cursor-pointer';
const txCard='rounded-2xl border border-[#dfe8f4] bg-white shadow-[0_8px_28px_rgba(3,45,96,0.045)]';
function TxSummary({label,value,meta,positive,Icon,accent,soft,delta}){
 const up=delta!=null&&delta>0;
 const down=delta!=null&&delta<0;
 return <div className={txCard+' flex min-w-0 items-center gap-3 p-4'}>
  <div className={'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl '+soft}>
   <Icon size={23} className={accent}/>
  </div>
  <div className="min-w-0 flex-1">
   <div className="flex flex-wrap items-center justify-between gap-1">
    <span className="text-xs font-medium text-slate-500">{label}</span>
    {delta!=null&&<span className={'rounded-full px-2 py-1 text-[10px] font-extrabold '+((positive?!down:!up)?'bg-emerald-50 text-emerald-700':'bg-rose-50 text-rose-600')}>
      {delta>0?'+':''}{delta.toFixed(1)}% {up?'↑':down?'↓':''}
    </span>}
   </div>
   <p className="mt-1 truncate text-[20px] font-extrabold leading-tight tracking-tight text-[#032d60]" title={value}>{value}</p>
   <p className="mt-1 text-[11px] text-slate-400">{meta}</p>
  </div>
 </div>;
}
const Transactions=()=>{
 const {state,actions}=useApp();
 const navigate=useNavigate(),location=useLocation();
 const {transactions=[],ious=[],categories=[],settings={},ui={}}=state;
 const initialQuery=new URLSearchParams(location.search).get('search')||'';
 const initialFilters=()=>initialQuery?{...BASE_FILTERS,datePreset:'all',searchText:initialQuery}:{...BASE_FILTERS};
 const accountingMode=ui.accountingMode||'accrual';
 const [draft,setDraft]=useState(initialFilters);
 const [applied,setApplied]=useState(initialFilters);
 const [validation,setValidation]=useState('');
 const [tab,setTab]=useState('all');
 const [sort,setSort]=useState({key:'date',direction:'desc'});
 const [rowsPerPage,setRowsPerPage]=useState(50);
 const [page,setPage]=useState(1);
 const [selectedTransactions,setSelectedTransactions]=useState([]);
 const [openMenu,setOpenMenu]=useState(null);
 const [showQuickAdd,setShowQuickAdd]=useState(false);
 const [showExportDialog,setShowExportDialog]=useState(false);
 const now=useMemo(()=>new Date(),[]);
 const categoryNames=useMemo(()=>[...new Set(categories.filter(c=>!c.isDeleted).map(c=>c.name).filter(Boolean))].sort(sortText.compare),[categories]);
 const accountNames=useMemo(()=>[...new Set(transactions.filter(t=>!t.isDeleted).map(t=>String(t.account||'Main Account')))].sort(sortText.compare),[transactions]);
 const dateRange=useMemo(()=>rangeFromPreset(applied,now),[applied,now]);
 const filteredBase=useMemo(()=>transactions.filter(t=>transactionMatches(t,applied,dateRange)),[transactions,applied,dateRange]);
 const counts=useMemo(()=>({
   all:filteredBase.length,
   income:filteredBase.filter(t=>t.type==='income').length,
   expense:filteredBase.filter(t=>t.type==='expense').length,
   recurring:filteredBase.filter(transactionIsRecurring).length
 }),[filteredBase]);
 const filteredTransactions=useMemo(()=>filteredBase.filter(t=>tab==='all'||
   (tab==='recurring'?transactionIsRecurring(t):t.type===tab)),[filteredBase,tab]);
 const sortedTransactions=useMemo(()=>[...filteredTransactions].sort((a,b)=>compareTransactionRows(a,b,sort)),[filteredTransactions,sort]);
 const pageCount=Math.max(1,Math.ceil(sortedTransactions.length/rowsPerPage));
 const safePage=Math.min(Math.max(1,page),pageCount);
 const pageRows=useMemo(()=>sortedTransactions.slice((safePage-1)*rowsPerPage,safePage*rowsPerPage),[sortedTransactions,safePage,rowsPerPage]);
 const pageIds=useMemo(()=>new Set(pageRows.map(t=>t.id)),[pageRows]);
 const selectedSet=useMemo(()=>new Set(selectedTransactions),[selectedTransactions]);
 const selectedVisible=pageRows.filter(t=>selectedSet.has(t.id)).length;
 const allPageSelected=pageRows.length>0&&selectedVisible===pageRows.length;
 const formatCurrency=amount=>new Intl.NumberFormat('en-AU',{style:'currency',currency:settings.currency||'AUD'}).format(Number(amount)||0);
 const dateLabel=date=>{
   const key=calendarDate(date);
   return key?format(new Date(key+'T12:00:00'),'d MMM yyyy'):'—';
 };
 const totals=useMemo(()=>{
   // The same engine and accrual/cash rules drive Dashboard, Budgets and Reports.
   const all=accountingEntries(filteredBase,accountingMode,ious);
   const entries=selectAccountingEntries(all,{start:dateRange?.start,end:dateRange?.end,
     view:ui.currentView||'current',mode:accountingMode,asOf:now});
   return summarizeTransactions(entries);
 },[filteredBase,ious,accountingMode,dateRange,ui.currentView,now]);
 const priorTotals=useMemo(()=>{
   if(!dateRange)return null;
   const from=new Date(dateRange.start+'T12:00:00');
   const to=new Date(dateRange.end+'T12:00:00');
   const days=Math.round((Date.parse(dateRange.end+'T00:00:00Z')-Date.parse(dateRange.start+'T00:00:00Z'))/86400000)+1;
   const previousStart=calendarDate(subDays(from,days));
   const previousEnd=calendarDate(subDays(from,1));
   const prior=transactions.filter(t=>transactionMatches(t,{...applied,datePreset:'all'}, {start:previousStart,end:previousEnd}));
   const entries=selectAccountingEntries(accountingEntries(prior,accountingMode,ious),{
     start:previousStart,end:previousEnd,view:ui.currentView||'current',mode:accountingMode,asOf:now});
   return summarizeTransactions(entries);
 },[transactions,applied,dateRange,ious,accountingMode,ui.currentView,now]);
 const changePercent=(current,previous)=>previous==null||Math.abs(previous)<0.001?null:
   Math.round(((current-previous)/Math.abs(previous))*1000)/10;
 // Honour global search and browser navigation without persisting search strings.
 useEffect(()=>{
  const value=new URLSearchParams(location.search).get('search')||'';
  const merge=current=>current.searchText===value?current:{
   ...current,searchText:value,
   datePreset:value&&current.datePreset==='thismonth'?'all':current.datePreset
  };
  setDraft(merge);setApplied(merge);
  setPage(1);setSelectedTransactions([]);setOpenMenu(null);
 },[location.search]);

 const updateDraft=(key,value)=>{setDraft(prev=>({...prev,[key]:value}));setValidation('')};
 const syncUrlSearch=value=>{
  const params=new URLSearchParams(location.search);
  if(value.trim())params.set('search',value);
  else params.delete('search');
  const next=params.toString()?'?'+params.toString():'';
  if(next!==location.search)navigate({pathname:'/transactions',search:next},{replace:true});
 };
 const updateSearch=value=>{
  const update=current=>({...current,searchText:value,
   datePreset:value.trim()&&current.datePreset==='thismonth'?'all':current.datePreset});
  setDraft(update);setApplied(update);setTab('all');setPage(1);
  setSelectedTransactions([]);setOpenMenu(null);setValidation('');
  syncUrlSearch(value);
 };
 const applyFilters=()=>{
   if(draft.datePreset==='custom'&&(!draft.customFrom||!draft.customTo||draft.customFrom>draft.customTo)){
     setValidation('Select a valid From and To date before applying this custom range.');return;
   }
   setApplied({...draft});setPage(1);setSelectedTransactions([]);setOpenMenu(null);setValidation('');
 };
 const clearFilters=()=>{
   const cleared={...BASE_FILTERS,datePreset:'all'};
   setDraft(cleared);setApplied(cleared);setTab('all');
   setPage(1);setSelectedTransactions([]);setOpenMenu(null);setValidation('');
   syncUrlSearch('');
 };
 const switchTab=value=>{setTab(value);setPage(1);setSelectedTransactions([]);setOpenMenu(null)};
 const changeSort=key=>{
   setSort(current=>current.key===key?{key,direction:current.direction==='asc'?'desc':'asc'}:
     {key,direction:key==='date'||key==='amount'?'desc':'asc'});
   setPage(1);setOpenMenu(null);
 };
 const toggleSelect=id=>setSelectedTransactions(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);
 const togglePage=()=>setSelectedTransactions(prev=>
   allPageSelected?prev.filter(id=>!pageIds.has(id)):
     [...new Set([...prev,...pageRows.map(t=>t.id)])]);
 const changePage=next=>{setPage(Math.min(Math.max(1,next),pageCount));setOpenMenu(null)};
 const changePageSize=value=>{setRowsPerPage(Number(value));setPage(1);setOpenMenu(null)};
 const deleteTransaction=async(id)=>{
   setOpenMenu(null);
   if(!window.confirm('Delete this transaction? This action also affects the linked Google backup when it next syncs.'))return;
   try{
     await dbService.deleteTransaction(id);
     actions.deleteTransaction(id);
     setSelectedTransactions(prev=>prev.filter(x=>x!==id));
   }catch(e){console.error('Failed to delete transaction',e);window.alert('Unable to delete this transaction. Your data was not changed by this screen.')}
 };
 const editTransaction=t=>{setOpenMenu(null);actions.setEditingTransaction(t)};
 const status=t=>t.status==='posted'?'Completed':'Pending';
 const statusPill=t=>t.status==='posted'
   ?'bg-emerald-50 text-emerald-700':'bg-amber-50 text-amber-700';
 const typePill=t=>t.type==='income'?'bg-emerald-50 text-emerald-700':'bg-rose-50 text-rose-600';
 const signedAmount=t=>(t.type==='income'?'+ ':'− ')+formatCurrency(Math.abs(Number(t.amount)||0));
 const currentLabel=applied.datePreset==='custom'&&dateRange
   ?dateLabel(dateRange.start)+' – '+dateLabel(dateRange.end)
   :PERIODS.find(p=>p.value===applied.datePreset)?.label||'All Dates';
 const paginationItems=useMemo(()=>{
   if(pageCount<=7)return Array.from({length:pageCount},(_,i)=>i+1);
   const mid=[1,2,pageCount-1,pageCount,safePage-1,safePage,safePage+1];
   const unique=[...new Set(mid.filter(v=>v>=1&&v<=pageCount))].sort((a,b)=>a-b);
   const result=[];
   unique.forEach((n,i)=>{if(i&&n-unique[i-1]>1)result.push('…-'+i);result.push(n)});
   return result;
 },[pageCount,safePage]);
 useEffect(()=>{
   const onOutside=e=>{if(!e.target.closest?.('[data-transaction-menu]'))setOpenMenu(null)};
   const onKey=e=>{if(e.key==='Escape')setOpenMenu(null)};
   document.addEventListener('pointerdown',onOutside);
   document.addEventListener('keydown',onKey);
   return ()=>{document.removeEventListener('pointerdown',onOutside);document.removeEventListener('keydown',onKey)};
 },[]);
 const tabs=[
   {key:'all',label:'All',Icon:FiIcons.FiLayers,count:counts.all},
   {key:'income',label:'Income',Icon:FiIcons.FiTrendingUp,count:counts.income},
   {key:'expense',label:'Expenses',Icon:FiIcons.FiTrendingDown,count:counts.expense},
   {key:'recurring',label:'Recurring',Icon:FiIcons.FiRepeat,count:counts.recurring}
 ];
 const headerSort=(key,label)=>{
   const selected=sort.key===key;
   return <button type="button" onClick={()=>changeSort(key)}
     aria-label={'Sort by '+label+(selected?', currently '+(sort.direction==='asc'?'ascending':'descending'):'')}
     className={'inline-flex items-center gap-1.5 rounded-lg px-1.5 py-1 transition-colors cursor-pointer hover:bg-blue-100 focus-visible:outline-2 focus-visible:outline-blue-600 '+(selected?'text-[#075ff0]':'text-[#2e4567]')}>
     <span>{label}</span>
     {selected?(sort.direction==='asc'?<FiIcons.FiArrowUp size={13}/>:<FiIcons.FiArrowDown size={13}/>):<span className="inline-flex flex-col items-center leading-none text-[#8193a9]" aria-hidden="true"><FiIcons.FiChevronUp size={9}/><FiIcons.FiChevronDown size={9}/></span>}
   </button>;
 };
 return <div className="space-y-4 pb-8 text-[#032d60]">
  <section aria-label="Transaction financial summary" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
   <TxSummary label="Total Income" value={formatCurrency(totals.income)}
     meta={priorTotals?'vs. previous period':'Recognised in this period'}
     delta={changePercent(totals.income,priorTotals?.income)} positive
     Icon={FiIcons.FiTrendingUp} accent="text-emerald-600" soft="bg-emerald-50"/>
   <TxSummary label="Total Expenses" value={formatCurrency(totals.expenses)}
     meta={priorTotals?'vs. previous period':'Recognised in this period'}
     delta={changePercent(totals.expenses,priorTotals?.expenses)} positive={false}
     Icon={FiIcons.FiTrendingDown} accent="text-rose-500" soft="bg-rose-50"/>
   <TxSummary label="Net" value={formatCurrency(totals.net)}
     meta={priorTotals?'vs. previous period':'Income less expenses'}
     delta={changePercent(totals.net,priorTotals?.net)} positive
     Icon={FiIcons.FiLayers} accent="text-blue-600" soft="bg-blue-50"/>
   <TxSummary label="Transactions" value={String(filteredBase.length)}
     meta={currentLabel} delta={changePercent(filteredBase.length,priorTotals?.transactions)}
     positive Icon={FiIcons.FiCalendar} accent="text-[#0176d3]" soft="bg-[#edf5ff]"/>
  </section>

  <div className="flex flex-wrap items-center justify-between gap-3">
   <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Transaction type">
    {tabs.map(item=><button key={item.key} type="button" role="tab" aria-selected={tab===item.key}
      onClick={()=>switchTab(item.key)}
      className={txButton+' border min-w-[112px] sm:min-w-[140px] '+(tab===item.key?
        'border-[#1766f4] bg-[#1766f4] text-white shadow-[0_5px_12px_rgba(23,102,244,0.2)]':
        'border-[#dce5f2] bg-white text-[#2c466b] hover:border-blue-300 hover:bg-blue-50')}>
      <item.Icon size={16}/>{item.label} ({item.count})
    </button>)}
   </div>
   <div className="flex flex-wrap items-center gap-2">
    <button type="button" className={txButton+' bg-[#1766f4] text-white hover:bg-[#0955da]'}
      onClick={()=>setShowQuickAdd(true)}><FiIcons.FiPlus size={16}/> Add Transaction</button>
    <button type="button" className={txButton+' border border-[#dce5f2] bg-white text-[#28456c] hover:bg-blue-50'}
      onClick={()=>navigate('/settings',{state:{openTab:'backup'}})}
      title="Import a full backup in Settings. Importing may replace existing records."><FiIcons.FiUpload size={15}/> Import</button>
    <button type="button" className={txButton+' border border-[#dce5f2] bg-white text-[#28456c] hover:bg-blue-50'}
      onClick={()=>setShowExportDialog(true)}><FiIcons.FiDownload size={15}/> Export</button>
   </div>
  </div>

  <section className={txCard+' p-3 sm:p-4'} aria-label="Transaction filters">
   <form className="flex min-w-0 flex-wrap items-end gap-2.5" onSubmit={e=>{e.preventDefault();applyFilters()}}>
    <label className="min-w-[164px] grow sm:grow-0 flex flex-col gap-1 text-[11px] font-semibold text-[#667b99]">Date
     <select aria-label="Transaction date range" value={draft.datePreset} onChange={e=>updateDraft('datePreset',e.target.value)} className={txSelect}>
      {PERIODS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
     </select>
    </label>
    {draft.datePreset==='custom'&&<>
     <label className="min-w-[143px] flex flex-col gap-1 text-[11px] font-semibold text-[#667b99]">From
      <input aria-label="Transaction start date" type="date" value={draft.customFrom} onChange={e=>updateDraft('customFrom',e.target.value)} className={txSelect}/>
     </label>
     <label className="min-w-[143px] flex flex-col gap-1 text-[11px] font-semibold text-[#667b99]">To
      <input aria-label="Transaction end date" type="date" value={draft.customTo} onChange={e=>updateDraft('customTo',e.target.value)} className={txSelect}/>
     </label>
    </>}
    <label className="min-w-[115px] flex-1 flex flex-col gap-1 text-[11px] font-semibold text-[#667b99]">Type
     <select aria-label="Transaction type filter" className={txSelect} value={draft.type} onChange={e=>updateDraft('type',e.target.value)}>
      <option value="">All Types</option><option value="income">Income</option><option value="expense">Expenses</option>
     </select>
    </label>
    <label className="min-w-[129px] flex-1 flex flex-col gap-1 text-[11px] font-semibold text-[#667b99]">Category
     <select aria-label="Transaction category filter" className={txSelect} value={draft.category} onChange={e=>updateDraft('category',e.target.value)}>
      <option value="">All Categories</option>{categoryNames.map(name=><option value={name} key={name}>{name}</option>)}
     </select>
    </label>
    <label className="min-w-[126px] flex-1 flex flex-col gap-1 text-[11px] font-semibold text-[#667b99]">Account
     <select aria-label="Transaction account filter" className={txSelect} value={draft.account} onChange={e=>updateDraft('account',e.target.value)}>
      <option value="">All Accounts</option>{accountNames.map(name=><option value={name} key={name}>{name}</option>)}
     </select>
    </label>
    <label className="min-w-[116px] flex-1 flex flex-col gap-1 text-[11px] font-semibold text-[#667b99]">Status
     <select aria-label="Transaction status filter" className={txSelect} value={draft.status} onChange={e=>updateDraft('status',e.target.value)}>
      <option value="">All Statuses</option><option value="posted">Completed</option><option value="pending">Pending</option>
     </select>
    </label>
    <label className="min-w-0 w-full flex-[1.4] flex flex-col gap-1 text-[11px] font-semibold text-[#667b99] sm:w-auto sm:min-w-[190px]">Search
     <span className="relative flex items-center"><FiIcons.FiSearch aria-hidden="true" className="absolute left-3 text-[#8195ad]"/>
      <input aria-label="Search transactions" type="search" value={draft.searchText} onChange={e=>updateSearch(e.target.value)}
       placeholder="Search merchant, category, person, notes..." className={txSelect+' w-full pl-9'}/></span>
    </label>
    <button type="button" onClick={clearFilters} className={txButton+' text-[#0756ce] hover:bg-blue-50'}>Clear</button>
    <button type="submit" className={txButton+' bg-[#1766f4] text-white hover:bg-[#0b55d1]'}><FiIcons.FiFilter size={15}/> Apply Filters</button>
   </form>
   {draft.searchText.trim()&&<div role="status" className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-blue-50 px-3 py-2.5 text-xs text-blue-800">
    <FiIcons.FiSearch size={15}/>
    <span className="min-w-0 flex-1">Showing <strong>{sortedTransactions.length}</strong> matching transaction{sortedTransactions.length===1?'':'s'} for <strong>“{draft.searchText.trim()}”</strong> {applied.datePreset==='all'?'across all dates':'in the selected date range'}.</span>
    <button type="button" onClick={()=>updateSearch('')} className="rounded-lg px-2 py-1 font-bold text-[#0756ce] hover:bg-blue-100">Clear search</button>
   </div>}
   {validation&&<p role="alert" className="mt-3 rounded-lg bg-amber-50 p-3 text-xs font-medium text-amber-800">{validation}</p>}
  </section>

  <section className={txCard+' overflow-hidden'} aria-label="Transactions list">
   {selectedTransactions.length>0&&<div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#e6edf6] bg-blue-50 px-4 py-3 text-xs font-semibold text-[#0d4f9e]">
    <span>{selectedTransactions.length} transaction{selectedTransactions.length===1?'':'s'} selected across pages</span>
    <div className="flex items-center gap-3">
     <button type="button" className="underline underline-offset-2 cursor-pointer" onClick={()=>setShowExportDialog(true)}>Export selected</button>
     <button type="button" className="underline underline-offset-2 cursor-pointer" onClick={()=>setSelectedTransactions([])}>Clear selection</button>
    </div>
   </div>}
   <div className="hidden xl:block overflow-x-auto">
    <table className="w-full min-w-[1080px] border-collapse text-left text-xs">
     <thead className="bg-[#f3f7fc]">
      <tr className="border-b border-[#e5edf7]">
       <th className="w-11 px-3 py-3"><input type="checkbox" aria-label="Select all transactions on this page" checked={allPageSelected}
        onChange={togglePage} disabled={!pageRows.length}
        className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-[#1766f4]"/></th>
       {TX_COLUMNS.map(col=><th key={col.key} scope="col"
        aria-sort={sort.key===col.key?(sort.direction==='asc'?'ascending':'descending'):'none'}
        className={'whitespace-nowrap px-2 py-3 font-bold '+(col.key==='amount'?'text-right':'')}>
        {headerSort(col.key,col.label)}
       </th>)}
       <th scope="col" className="w-12 px-2 py-3 text-right"><span className="sr-only">Actions</span></th>
      </tr>
     </thead>
     <tbody>
      {pageRows.map((t,index)=><tr key={t.id}
       className={'border-b border-[#edf1f6] transition-colors hover:bg-[#edf5ff] '+(selectedSet.has(t.id)?'bg-[#eaf3ff]':index%2?'bg-[#f8fafc]':'bg-white')}>
       <td className="px-3 py-3 align-middle"><input type="checkbox" aria-label={'Select transaction '+(t.payee||t.id)}
        checked={selectedSet.has(t.id)} onChange={()=>toggleSelect(t.id)}
        className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-[#1766f4]"/></td>
       <td className="whitespace-nowrap px-3 py-3 font-medium text-[#19355a]">{dateLabel(t.date)}</td>
       <td className="px-2 py-3"><span className={'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold '+typePill(t)}>
        {t.type==='income'?<FiIcons.FiArrowUp size={12}/>:<FiIcons.FiArrowDown size={12}/>}
        {t.type==='income'?'Income':'Expense'}</span></td>
       <td className="px-2 py-3"><div className="flex items-center gap-2 whitespace-nowrap text-[#203a61]">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[#eaf2ff] text-blue-600"><FiIcons.FiGrid size={13}/></span>
        <span className="max-w-[135px] truncate" title={t.category}>{t.category||'Uncategorised'}</span>
       </div></td>
       <td className="max-w-[200px] truncate px-3 py-3 font-medium text-[#203858]" title={t.payee||''}>{t.payee||'—'}</td>
       <td className={'whitespace-nowrap px-3 py-3 text-right font-extrabold '+(t.type==='income'?'text-[#079447]':'text-[#f02d50]')}>
        {signedAmount(t)}</td>
       <td className="whitespace-nowrap px-2 py-3 text-[#2b4263]"><span className="inline-flex items-center gap-1.5"><FiIcons.FiCreditCard size={14}/>{t.account||'Main Account'}</span></td>
       <td className="px-2 py-3"><span className={'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold '+statusPill(t)}>
        {t.status==='posted'?<FiIcons.FiCheckCircle size={12}/>:<FiIcons.FiClock size={12}/>}
        {status(t)}</span></td>
       <td className="max-w-[160px] truncate px-2 py-3 text-[#526782]" title={[t.notes,t.person].filter(Boolean).join(' · ')}>
        {t.notes||t.person||'—'}</td>
       <td className="relative px-2 py-3 text-right" data-transaction-menu>
        <button type="button" aria-label={'More options for '+(t.payee||t.id)}
         aria-expanded={openMenu===t.id} onClick={()=>setOpenMenu(openMenu===t.id?null:t.id)}
         className="inline-grid h-8 w-8 place-items-center rounded-lg text-[#435f84] hover:bg-blue-100 cursor-pointer focus-visible:outline-2 focus-visible:outline-blue-600">
         <FiIcons.FiMoreVertical size={17}/>
        </button>
        {openMenu===t.id&&<div className={'absolute right-2 z-30 w-36 rounded-xl border border-[#e2eaf3] bg-white p-1.5 shadow-xl '+(index>pageRows.length-4?'bottom-[94%]':'top-[90%]')}>
         <button type="button" onClick={()=>editTransaction(t)}
          className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold text-[#244a75] hover:bg-blue-50">
          <FiIcons.FiEdit2 size={13}/> Edit</button>
         <button type="button" onClick={()=>deleteTransaction(t.id)}
          className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold text-rose-600 hover:bg-rose-50">
          <FiIcons.FiTrash2 size={13}/> Delete</button>
        </div>}
       </td>
      </tr>)}
     </tbody>
    </table>
   </div>
   <div className="xl:hidden divide-y divide-[#edf1f6]">
    {pageRows.map(t=><article key={t.id} className={'space-y-2 p-4 '+(selectedSet.has(t.id)?'bg-[#f0f6ff]':'bg-white')}>
     <div className="flex items-start justify-between gap-2">
      <div className="flex min-w-0 items-start gap-3">
       <input type="checkbox" aria-label={'Select transaction '+(t.payee||t.id)} checked={selectedSet.has(t.id)}
        onChange={()=>toggleSelect(t.id)} className="mt-1 h-4 w-4 accent-[#1766f4]"/>
       <div className="min-w-0"><p className="break-words text-sm font-bold text-[#0a2e62]">{t.payee||t.category||'Transaction'}</p>
        <p className="text-xs text-[#7b8eaa]">{dateLabel(t.date)} · {t.category||'Uncategorised'}</p></div>
      </div>
      <span className={'shrink-0 text-sm font-extrabold '+(t.type==='income'?'text-emerald-600':'text-rose-500')}>{signedAmount(t)}</span>
     </div>
     <div className="flex flex-wrap items-center gap-2 pl-7 text-[11px] text-[#546c8c]">
      <span className={'rounded-full px-2 py-1 font-semibold '+typePill(t)}>{t.type==='income'?'Income':'Expense'}</span>
      <span className={'rounded-full px-2 py-1 font-semibold '+statusPill(t)}>{status(t)}</span>
      <span className="inline-flex items-center gap-1"><FiIcons.FiCreditCard/>{t.account||'Main Account'}</span>
      {(t.notes||t.person)&&<span className="basis-full truncate" title={[t.notes,t.person].filter(Boolean).join(' · ')}>{t.notes||t.person}</span>}
     </div>
     <div className="flex justify-end gap-2 pl-7">
      <button type="button" aria-label={'Edit '+(t.payee||t.id)} onClick={()=>editTransaction(t)}
       className={txButton+' border border-blue-100 bg-blue-50 text-blue-700'}><FiIcons.FiEdit2/> Edit</button>
      <button type="button" aria-label={'Delete '+(t.payee||t.id)} onClick={()=>deleteTransaction(t.id)}
       className={txButton+' border border-rose-100 bg-rose-50 text-rose-600'}><FiIcons.FiTrash2/> Delete</button>
     </div>
    </article>)}
   </div>
   {sortedTransactions.length===0&&<div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
    <div className="grid h-14 w-14 place-items-center rounded-2xl bg-blue-50 text-blue-600"><FiIcons.FiInbox size={24}/></div>
    <h3 className="text-base font-bold text-[#032d60]">No transactions found</h3>
    <p className="max-w-sm text-xs leading-5 text-[#687f9f]">{draft.searchText.trim()?'No records match this search. Try fewer words or clear your search.':'Try a different date or filter, or add your first transaction.'}</p>
    {draft.searchText.trim()?<button type="button" onClick={()=>updateSearch('')} className={txButton+' mt-2 bg-[#1766f4] text-white'}>Clear search</button>:
    <button type="button" onClick={()=>setShowQuickAdd(true)}
     className={txButton+' mt-2 bg-[#1766f4] text-white'}><FiIcons.FiPlus/> Add Transaction</button>}
   </div>}
   <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-[#e6edf6] bg-white px-4 py-4">
    <div className="flex flex-wrap items-center gap-3 text-xs text-[#6b7e9a]">
     <label className="inline-flex items-center gap-2" htmlFor="transaction-page-size">Rows per page:
      <select id="transaction-page-size" aria-label="Rows per page" value={rowsPerPage}
       onChange={e=>changePageSize(e.target.value)} className="cursor-pointer rounded-lg border border-[#d5e2f4] bg-white px-2.5 py-2 font-bold text-[#254b85]">
       <option value={50}>50</option><option value={100}>100</option><option value={200}>200</option>
      </select></label>
     <span role="status">{sortedTransactions.length?
      'Showing '+((safePage-1)*rowsPerPage+1)+' – '+Math.min(sortedTransactions.length,safePage*rowsPerPage)+' of '+sortedTransactions.length+' transactions':
      'Showing 0 of 0 transactions'}</span>
    </div>
    <nav className="flex min-w-0 w-full flex-wrap items-center justify-center gap-1.5 sm:w-auto sm:justify-start" aria-label="Transactions pagination">
     <button type="button" disabled={safePage===1} onClick={()=>changePage(safePage-1)}
      className={txButton+' border border-[#e1e9f5] bg-white text-[#33597d] disabled:opacity-40 disabled:cursor-not-allowed'}><FiIcons.FiChevronLeft size={14}/> Previous</button>
     {paginationItems.map(n=>typeof n==='string'?<span key={n} className="px-1 text-xs text-[#9daec4]">…</span>:
      <button type="button" key={n} aria-label={'Page '+n} aria-current={n===safePage?'page':undefined} onClick={()=>changePage(n)}
       className={txButton+' min-w-9 px-2.5 '+(n===safePage?'bg-[#1766f4] text-white shadow-sm':'bg-white text-[#294c75] hover:bg-blue-50')}>
       {n}</button>)}
     <button type="button" disabled={safePage===pageCount} onClick={()=>changePage(safePage+1)}
      className={txButton+' border border-[#e1e9f5] bg-white text-[#33597d] disabled:opacity-40 disabled:cursor-not-allowed'}>Next <FiIcons.FiChevronRight size={14}/></button>
    </nav>
   </footer>
  </section>
  <div className="flex flex-wrap items-center gap-2 text-xs text-[#6b809d]">
   <span>Totals basis:</span>
   <button type="button" aria-pressed={accountingMode==='accrual'} onClick={()=>actions.setAccountingMode('accrual')}
    className={txButton+' min-h-8 px-3 py-1.5 '+(accountingMode==='accrual'?'bg-blue-600 text-white':'border border-blue-100 bg-white text-blue-700')}>Accrual</button>
   <button type="button" aria-pressed={accountingMode==='cash'} onClick={()=>actions.setAccountingMode('cash')}
    className={txButton+' min-h-8 px-3 py-1.5 '+(accountingMode==='cash'?'bg-blue-600 text-white':'border border-blue-100 bg-white text-blue-700')}>Cash</button>
   <span className="ml-auto">Sorting, filtering, selection and pagination operate in your browser; they do not modify saved data.</span>
  </div>
  <AnimatePresence>
   {showQuickAdd&&<QuickAddForm onClose={()=>setShowQuickAdd(false)}/>}
   {state.ui.editingTransaction&&<TransactionEditForm transaction={state.ui.editingTransaction}
     onClose={()=>actions.setEditingTransaction(null)}/>}
   {state.ui.showCategoryManager&&<CategoryManager onClose={actions.toggleCategoryManager}/>}
   {showExportDialog&&<ExportDialog transactions={transactions.filter(t=>!t.isDeleted)}
      visibleTransactions={filteredTransactions} filters={{...applied,dateRange:dateRange?{
        start:new Date(dateRange.start+'T00:00:00'),end:new Date(dateRange.end+'T23:59:59')}:null}}
      selectedTransactions={selectedTransactions} settings={settings} onClose={()=>setShowExportDialog(false)}/>}
  </AnimatePresence>
 </div>;
};
export default Transactions;